import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormCachingService } from '../../utilities';
import { FeatureRequestDraft, FeatureRequestError, FeatureRequestPage } from '../feature-request.model';
import { FeatureRequestService } from '../feature-request.service';
import { ideasBaseRoute, ideasHubSlug, nameHubInBreadcrumb } from '../ideas-context';

/** The form's values as kept by the form caching service. */
interface CachedIdeaForm {
  name: string;
  originalRequest: string;
  target: string;
}

/** Where the form stops a reader, matching the backend's limits. */
export const IDEA_NAME_MAX = 120;
export const IDEA_WORDS_MAX = 4000;

/** `Validators.required` accepts spaces alone; the backend does not. */
function notBlank(control: AbstractControl<string>): ValidationErrors | null {
  return control.value.trim() === '' ? { blank: true } : null;
}

/**
 * Submitting an idea, in the reader's own words. It arrives pending: only
 * they and our staff see it until it is approved.
 *
 * **Where it goes** follows what the backend says this reader may do
 * (`meta.can_create`): in a hub's view, private to that hub; in the public
 * area, public for an Interculturalist, or private to one of the hubs they
 * own, manage or administer — the Ideas plan, decision 3.14.
 *
 * **Prefilled after "withdraw and start again"**: the detail page passes the
 * withdrawn words in the navigation state, and nothing is stored on the
 * server — decision 3.20.
 *
 * **A draft survives a reload**: the form caching service keeps the form on
 * every change, one draft per hub view and one for the public area, until the
 * idea is submitted. Words arriving from "withdraw and start again" replace a
 * kept draft; after a reload, the kept draft wins.
 */
@Component({
  selector: 'multisite-idea-submit',
  templateUrl: './idea-submit.component.html',
  imports: [ReactiveFormsModule, RouterModule, TranslocoModule],
  providers: [provideTranslocoScope('ideas')],
})
export class IdeaSubmitComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private ideas = inject(FeatureRequestService);
  private formBuilder = inject(NonNullableFormBuilder);
  private formCachingService = inject(FormCachingService);

  readonly hub = ideasHubSlug(this.route);
  private readonly formCacheKey = `idea_new_${this.hub ?? 'public'}`;
  private readonly nameHub = nameHubInBreadcrumb(this.hub);
  readonly baseRoute = ideasBaseRoute(this.hub);
  readonly nameMax = IDEA_NAME_MAX;
  readonly wordsMax = IDEA_WORDS_MAX;

  readonly form = this.formBuilder.group({
    name: ['', [Validators.required, notBlank, Validators.maxLength(IDEA_NAME_MAX)]],
    originalRequest: ['', [Validators.required, notBlank, Validators.maxLength(IDEA_WORDS_MAX)]],
    /** `''` is public; otherwise a hub slug. */
    target: [''],
  });

  canCreate = signal<FeatureRequestPage['canCreate'] | null>(null);
  submitting = signal(false);
  failure = signal<FeatureRequestError | null>(null);
  restarted = signal(false);

  targets = computed(() => {
    const canCreate = this.canCreate();

    if (!canCreate) {
      return [];
    }

    if (this.hub) {
      return canCreate.hubs.filter((hub) => hub.slug === this.hub);
    }

    return canCreate.hubs;
  });

  mayCreatePublic = computed(() => !this.hub && !!this.canCreate()?.public);
  maySubmitAtAll = computed(() => this.mayCreatePublic() || this.targets().length > 0);

  private subscriptions: Subscription[] = [];

  constructor() {
    // Kept from the first change on, including the words restored below.
    this.subscriptions.push(this.form.valueChanges.subscribe(() => this.cacheForm()));

    // Withdrawn words arriving with this navigation come first. The browser keeps the same words
    // in its history across a reload, so after a reload a kept draft, holding any later edits,
    // comes before them.
    const arriving = this.router.getCurrentNavigation()?.extras.state?.['ideaDraft'] as Partial<FeatureRequestDraft> | undefined;
    const cached: Partial<CachedIdeaForm> | null = arriving ? null : this.formCachingService.getDataItem(this.formCacheKey);
    const draft = arriving ?? (cached ? undefined : (history.state?.['ideaDraft'] as Partial<FeatureRequestDraft> | undefined));

    if (draft) {
      this.form.patchValue({ name: draft.name ?? '', originalRequest: draft.originalRequest ?? '' });
      this.restarted.set(true);
    } else if (cached) {
      this.form.patchValue({ name: cached.name ?? '', originalRequest: cached.originalRequest ?? '' });
    }

    this.subscriptions.push(
      this.ideas.getPage({ pageSize: 1, hub: this.hub ?? undefined }).subscribe({
        next: (page) => {
          this.canCreate.set(page.canCreate);
          this.nameHub(page.hub?.name);
          this.form.controls.target.setValue(this.hub ?? this.keptTarget(cached?.target) ?? (page.canCreate.public ? '' : (page.canCreate.hubs[0]?.slug ?? '')));
        },
        error: (failure: FeatureRequestError) => this.failure.set(failure),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /** Red once the reader has left the field, not while they are still typing their first word. */
  isInvalid(control: 'name' | 'originalRequest'): boolean {
    const field = this.form.controls[control];

    return field.invalid && field.touched;
  }

  /** A kept choice of where the idea goes, if this reader may still choose it. */
  private keptTarget(target: string | undefined): string | null {
    if (target === undefined) {
      return null;
    }

    const stillAllowed = target === '' ? this.mayCreatePublic() : this.targets().some((hub) => hub.slug === target);

    return stillAllowed ? target : null;
  }

  /** Nothing is kept for a form with no words in it. */
  private cacheForm(): void {
    const value: CachedIdeaForm = this.form.getRawValue();

    if (value.name.trim() === '' && value.originalRequest.trim() === '') {
      this.formCachingService.clearDataItem(this.formCacheKey);
      return;
    }

    this.formCachingService.cacheFormData(value, this.formCacheKey);
  }

  submit(): void {
    if (this.submitting() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, originalRequest, target } = this.form.getRawValue();

    this.submitting.set(true);
    this.failure.set(null);

    this.subscriptions.push(
      this.ideas
        .submit({ name, originalRequest, hub: target || null })
        .pipe(finalize(() => this.submitting.set(false)))
        .subscribe({
          next: (idea) => {
            this.formCachingService.clearDataItem(this.formCacheKey);
            this.router.navigate([...this.baseRoute, idea.slug]);
          },
          error: (failure: FeatureRequestError) => this.failure.set(failure),
        }),
    );
  }
}
